import { FormFieldDto, LearnerFieldAnswer } from './dtos';
import { hasExpectedAnswer, isMultiChoiceType, selectionsMatch } from '../components/content-viewer/multi-choice-fields';
import { readableAnswer } from '../components/content-viewer/fragment-answers';

export interface LearnerComparisonPair {
    earlier_field: FormFieldDto;
    later_field: FormFieldDto;
    earlier_display_field: FormFieldDto;
    earlier_answered: boolean;
    earlier_answer: LearnerFieldAnswer | null;
    earlier_answered_at: string | null;
    later_answer?: LearnerFieldAnswer | null;
    later_answered_at?: string | null;
    earlier_settings_changed: boolean;
}

/** Saved answer timestamps are UTC; current edits must never inherit a saved date. */
export function comparisonAnswerHeaders(comparison: LearnerFormComparison, current: Record<string, unknown>) {
    const datedHeader = (timestamps: (string | null | undefined)[], fallback: string) => {
        if (!timestamps.length || timestamps.some(value => !value)) return fallback;
        const dates = timestamps.map(value => new Date(value!.replace(' ', 'T') + (/Z$|[+-]\d{2}:\d{2}$/.test(value!) ? '' : 'Z')));
        if (dates.some(date => Number.isNaN(date.getTime()))) return fallback;
        const times = dates.map(date => date.getTime());
        const first = new Date(Math.min(...times)).toLocaleString();
        const last = new Date(Math.max(...times)).toLocaleString();
        return `Answered on ${first === last ? first : `${first} – ${last}`}`;
    };
    const canonical = (value: unknown): string | undefined => JSON.stringify(value && typeof value === 'object' && !Array.isArray(value)
        ? Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b))) : Array.isArray(value) ? [...value].sort() : value);
    const savedCurrent = comparison.fields.every(pair => pair.later_answered_at && canonical(pair.later_answer) === canonical(current[pair.later_field.key]));
    return {
        earlier: datedHeader(comparison.fields.filter(pair => pair.earlier_answered).map(pair => pair.earlier_answered_at), 'Saved answers'),
        later: savedCurrent ? datedHeader(comparison.fields.map(pair => pair.later_answered_at), 'Current answers') : 'Current answers',
    };
}
export interface LearnerFormComparison {
    id: number;
    course_key: string;
    course_name: string;
    earlier_fragment_key: string;
    earlier_fragment_title: string;
    earlier_module_name: string;
    later_fragment_title: string;
    later_module_name: string;
    fields: LearnerComparisonPair[];
}

export function hasComparisonAnswer(value: unknown): boolean {
    // An explicitly unchecked checkbox and an empty multi-choice selection are valid answers.
    return value !== undefined && value !== null && (typeof value !== 'string' || value.trim() !== '');
}

export function comparisonAnswerText(field: FormFieldDto, value: unknown): string {
    if (field.type === 'inline-text' && typeof field.expectedValue === 'string' && !field.expectedValue.trim() && value === '') return 'Left blank';
    if (!hasComparisonAnswer(value)) return 'Not answered';
    if (field.type === 'quiz-list' && value && typeof value === 'object' && !Array.isArray(value)) {
        return Object.entries(value).map(([key, answer]) => `${field.options?.find(option => option.key === key)?.value ?? key}: ${readableAnswer(field, answer) || 'Not answered'}`).join('\n');
    }
    if (isMultiChoiceType(field.type) && Array.isArray(value) && !value.length) return 'No options selected';
    return readableAnswer(field, value);
}

/** Use answer keys only where deterministic assessment is appropriate; do not invent AI/history scores. */
export function comparisonCorrectness(field: FormFieldDto, value: unknown): boolean | null {
    if (field.type === 'inline-text' && typeof field.expectedValue === 'string' && !field.expectedValue.trim() && value === '') return true;
    if (!hasComparisonAnswer(value) || field.type === 'text' || field.type === 'textarea' || field.type === 'quiz-list'
        || !hasExpectedAnswer(field.expectedValue, field.type)) return null;
    if (isMultiChoiceType(field.type)) return selectionsMatch(value, field.expectedValue);
    const normalize = (v: unknown) => String(v ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
    if (field.type === 'checkbox') {
        const expected = normalize(field.expectedValue);
        return expected === 'true' || expected === 'false' ? (value === true || value === 'true') === (expected === 'true') : null;
    }
    if (field.type === 'select' || field.type === 'radio') {
        const label = (v: unknown) => field.options?.find(option => option.key === String(v))?.value ?? v;
        return normalize(label(value)) === normalize(label(field.expectedValue));
    }
    return normalize(value) === normalize(field.expectedValue);
}

export function comparisonSummary(comparison: LearnerFormComparison, current: Record<string, unknown>) {
    let assessed = 0, earlierCorrect = 0, laterCorrect = 0;
    for (const pair of comparison.fields) {
        if (!pair.earlier_answered || pair.earlier_settings_changed) continue;
        const earlier = comparisonCorrectness(pair.earlier_field, pair.earlier_answer);
        const later = comparisonCorrectness(pair.later_field, current[pair.later_field.key]);
        if (earlier === null || later === null) continue;
        assessed++; earlierCorrect += Number(earlier); laterCorrect += Number(later);
    }
    return { assessed, earlierCorrect, laterCorrect, change: laterCorrect - earlierCorrect };
}
